import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Star, StarOff, Eye, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Story {
  id: string;
  title: string;
  content: string;
  short_description: string | null;
  image_url: string | null;
  audio_url: string | null;
  is_active: boolean;
  created_at: string;
  meta_title?: string | null;
  meta_description?: string | null;
  slug?: string | null;
  tags?: string[] | null;
  image_alt_text?: string | null;
  language?: string;
}

interface StoryFormData {
  title: string;
  content: string;
  short_description: string;
  image_url: string;
  audio_url: string;
  is_active: boolean;
  meta_title: string;
  meta_description: string;
  slug: string;
  tags: string;
  image_alt_text: string;
  language: string;
}

const StoryManager = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [filteredStories, setFilteredStories] = useState<Story[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('All');
  const [storyOfTheDayId, setStoryOfTheDayId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStory, setEditingStory] = useState<Story | null>(null);
  const [formData, setFormData] = useState<StoryFormData>({
    title: '',
    content: '',
    short_description: '',
    image_url: '',
    audio_url: '',
    is_active: true,
    meta_title: '',
    meta_description: '',
    slug: '',
    tags: '',
    image_alt_text: '',
    language: 'English',
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchStories();
    fetchStoryOfTheDay();
  }, []);

  useEffect(() => {
    filterStories();
  }, [stories, selectedLanguage]);

  const filterStories = () => {
    if (selectedLanguage === 'All') {
      setFilteredStories(stories);
    } else {
      setFilteredStories(stories.filter(story => story.language === selectedLanguage));
    }
  };

  const getUniqueLanguages = () => {
    const languages = [...new Set(stories.map(story => story.language || 'English'))];
    return ['All', ...languages.sort()];
  };

  const fetchStories = async () => {
    try {
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setStories(data || []);
    } catch (error) {
      console.error('Error fetching stories:', error);
      toast({
        title: "Error",
        description: "Failed to fetch stories",
        variant: "destructive",
      });
    }
  };

  const fetchStoryOfTheDay = async () => {
    try {
      const { data, error } = await supabase
        .from('story_of_the_day')
        .select('story_id')
        .eq('is_active', true)
        .limit(1);

      if (error) throw error;
      if (data && data.length > 0) {
        setStoryOfTheDayId(data[0].story_id);
      }
    } catch (error) {
      console.error('Error fetching story of the day:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingStory) {
        const { error } = await supabase
          .from('stories')
          .update({
            title: formData.title,
            content: formData.content,
            short_description: formData.short_description || null,
            image_url: formData.image_url || null,
            audio_url: formData.audio_url || null,
            is_active: formData.is_active,
            meta_title: formData.meta_title || null,
            meta_description: formData.meta_description || null,
            slug: formData.slug || null,
            tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0) : null,
            image_alt_text: formData.image_alt_text || null,
            language: formData.language,
          })
          .eq('id', editingStory.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Story updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('stories')
          .insert({
            title: formData.title,
            content: formData.content,
            short_description: formData.short_description || null,
            image_url: formData.image_url || null,
            audio_url: formData.audio_url || null,
            is_active: formData.is_active,
            meta_title: formData.meta_title || null,
            meta_description: formData.meta_description || null,
            slug: formData.slug || null,
            tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0) : null,
            image_alt_text: formData.image_alt_text || null,
            language: formData.language,
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Story created successfully",
        });
      }

      setIsDialogOpen(false);
      setEditingStory(null);
      resetForm();
      fetchStories();
    } catch (error) {
      console.error('Error saving story:', error);
      toast({
        title: "Error",
        description: "Failed to save story",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (storyId: string) => {
    try {
      const { error } = await supabase
        .from('stories')
        .delete()
        .eq('id', storyId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Story deleted successfully",
      });
      
      fetchStories();
      if (storyOfTheDayId === storyId) {
        setStoryOfTheDayId(null);
      }
    } catch (error) {
      console.error('Error deleting story:', error);
      toast({
        title: "Error",
        description: "Failed to delete story",
        variant: "destructive",
      });
    }
  };

  const handleSetStoryOfTheDay = async (storyId: string) => {
    try {
      // First deactivate current story of the day
      await supabase
        .from('story_of_the_day')
        .update({ is_active: false })
        .eq('is_active', true);

      // Then set new story of the day
      const { error } = await supabase
        .from('story_of_the_day')
        .insert({
          story_id: storyId,
          is_active: true,
        });

      if (error) throw error;

      setStoryOfTheDayId(storyId);
      toast({
        title: "Success",
        description: "Story of the day updated successfully",
      });
    } catch (error) {
      console.error('Error setting story of the day:', error);
      toast({
        title: "Error",
        description: "Failed to set story of the day",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (story: Story) => {
    setEditingStory(story);
    setFormData({
      title: story.title,
      content: story.content,
      short_description: story.short_description || '',
      image_url: story.image_url || '',
      audio_url: story.audio_url || '',
      is_active: story.is_active,
      meta_title: story.meta_title || '',
      meta_description: story.meta_description || '',
      slug: story.slug || '',
      tags: story.tags ? story.tags.join(', ') : '',
      image_alt_text: story.image_alt_text || '',
      language: story.language || 'English',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      short_description: '',
      image_url: '',
      audio_url: '',
      is_active: true,
      meta_title: '',
      meta_description: '',
      slug: '',
      tags: '',
      image_alt_text: '',
      language: 'English',
    });
  };

  const openCreateDialog = () => {
    setEditingStory(null);
    resetForm();
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-tinyminds-purple">Story Management</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-tinyminds-purple" />
            <Label htmlFor="language-filter" className="text-sm font-medium">Filter by Language:</Label>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                {getUniqueLanguages().map((language) => (
                  <SelectItem key={language} value={language}>{language}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="bg-tinyminds-purple hover:bg-tinyminds-purple/90">
              <Plus className="w-4 h-4 mr-2" />
              Add New Story
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingStory ? 'Edit Story' : 'Create New Story'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="short_description">Short Description</Label>
                <Textarea
                  id="short_description"
                  value={formData.short_description}
                  onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                  rows={3}
                  placeholder="A brief description that will appear in the story list"
                />
              </div>

              <div>
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <Label htmlFor="audio_url">Audio URL</Label>
                <Input
                  id="audio_url"
                  type="url"
                  value={formData.audio_url}
                  onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                  placeholder="https://example.com/audio.mp3"
                />
              </div>

              <div>
                <Label htmlFor="image_alt_text">Image ALT Text</Label>
                <Input
                  id="image_alt_text"
                  value={formData.image_alt_text}
                  onChange={(e) => setFormData({ ...formData, image_alt_text: e.target.value })}
                  placeholder="Descriptive text for the image"
                />
              </div>

              <div>
                <Label htmlFor="language">Language *</Label>
                <Select value={formData.language} onValueChange={(value) => setFormData({ ...formData, language: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="English">English</SelectItem>
                    <SelectItem value="Hindi">Hindi</SelectItem>
                    <SelectItem value="Spanish">Spanish</SelectItem>
                    <SelectItem value="French">French</SelectItem>
                    <SelectItem value="German">German</SelectItem>
                    <SelectItem value="Chinese">Chinese</SelectItem>
                    <SelectItem value="Japanese">Japanese</SelectItem>
                    <SelectItem value="Korean">Korean</SelectItem>
                    <SelectItem value="Arabic">Arabic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="content">Story Content *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={10}
                  placeholder="Write your story here..."
                />
              </div>

              {/* SEO Fields */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-semibold text-tinyminds-purple">SEO Settings</h3>
                
                <div>
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder="SEO-optimized title (recommended: 50-60 characters)"
                    maxLength={60}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.meta_title.length}/60 characters
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <Textarea
                    id="meta_description"
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Brief description for search engines (recommended: 150-160 characters)"
                    rows={3}
                    maxLength={160}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.meta_description.length}/160 characters
                  </p>
                </div>

                <div>
                  <Label htmlFor="slug">URL Slug</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') })}
                    placeholder="url-friendly-story-title"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL will be: /stories/{formData.slug || 'story-slug'}
                  </p>
                </div>

                <div>
                  <Label htmlFor="tags">Tags / Keywords</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="adventure, children, bedtime, fantasy (separated by commas)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separate multiple tags with commas
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex space-x-2">
                <Button type="submit" className="bg-tinyminds-purple hover:bg-tinyminds-purple/90">
                  {editingStory ? 'Update Story' : 'Create Story'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStories.map((story) => (
          <Card key={story.id} className="relative">
            {storyOfTheDayId === story.id && (
              <div className="absolute -top-2 -right-2 z-10">
                <div className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center">
                  <Star className="w-3 h-3 mr-1" />
                  Story of the Day
                </div>
              </div>
            )}
            
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg text-tinyminds-purple line-clamp-2">
                    {story.title}
                  </CardTitle>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      story.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {story.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {story.audio_url && (
                      <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                        🎵 Audio
                      </span>
                    )}
                    <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                      {story.language || 'English'}
                    </span>
                  </div>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              {story.image_url && (
                <img
                  src={story.image_url}
                  alt={story.title}
                  className="w-full h-32 object-cover rounded"
                />
              )}
              
              {story.short_description && (
                <p className="text-sm text-gray-600 line-clamp-3">
                  {story.short_description}
                </p>
              )}
              
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => handleEdit(story)}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                
                <Button
                  onClick={() => handleSetStoryOfTheDay(story.id)}
                  size="sm"
                  variant="outline"
                  className={`flex-1 ${
                    storyOfTheDayId === story.id 
                      ? 'bg-yellow-100 border-yellow-500 text-yellow-700' 
                      : ''
                  }`}
                  disabled={storyOfTheDayId === story.id}
                >
                  {storyOfTheDayId === story.id ? (
                    <>
                      <StarOff className="w-3 h-3 mr-1" />
                      Featured
                    </>
                  ) : (
                    <>
                      <Star className="w-3 h-3 mr-1" />
                      Feature
                    </>
                  )}
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Story</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{story.title}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(story.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stories.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Eye className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Stories Yet</h3>
          <p className="text-gray-500">Create your first story to get started!</p>
        </div>
      )}
    </div>
  );
};

export default StoryManager;
